import { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { History } from 'history';
import { useTranslation } from 'react-i18next';
import { DropdownItemElementType } from '@pwc/pwc-deals-ui';
import { NavigationNodeId } from '@pwc/pwc-deals-web-common';

import { getProject } from 'features/project/projectSlice';
import {
  analysesCustomGroupsListPath,
  analysesKFAsGroupsListPath,
  analysesNoneGroupsListPath,
} from '../components/AnalysesGroupsContentRouter/AnalysesGroupsContentRouter';
import { Dropdown } from '../models/Dropdown';
import {
  GROUP_SETTING_KEY,
  saveUserPreferences,
} from '../../../common/services/userPreferencesProjectService';
import {
  CUSTOM_GROUP_NAME,
  KFA_GROUP_NAME,
  NONE_GROUP_NAME,
} from '../models/constants';

type GroupByKey = 'Custom' | 'Key Focus Area' | 'None';

export const GROUP_BY_I18N_KEY = {
  Custom: 'groupBy.custom',
  'Key Focus Area': 'groupBy.KFA',
  None: 'groupBy.none',
};

const useGroupDropdown = (defaultGroupBy: GroupByKey = 'None'): Dropdown => {
  const history: History = useHistory();
  const { id: projectId, navigation = [] } = useSelector(getProject);
  const { t } = useTranslation();
  const [groupBy, setGroupBy] = useState(defaultGroupBy);

  async function savePreferences(group: string) {
    return saveUserPreferences(projectId, GROUP_SETTING_KEY, group);
  }

  function isSectionHidden(id: NavigationNodeId) {
    return navigation.find((nav) => nav.id === id)?.isHidden;
  }

  function getSectionTitle(id: NavigationNodeId) {
    return navigation.find((nav) => nav.id === id)?.title;
  }

  function onOptionClick(group: GroupByKey) {
    return () => {
      setGroupBy(group);

      switch (group) {
        case 'Custom': {
          void savePreferences(CUSTOM_GROUP_NAME);
          return history.push(analysesCustomGroupsListPath(projectId));
        }
        case 'None': {
          void savePreferences(NONE_GROUP_NAME);
          return history.push(analysesNoneGroupsListPath(projectId));
        }
        case 'Key Focus Area': {
          void savePreferences(KFA_GROUP_NAME);
          return history.push(analysesKFAsGroupsListPath(projectId));
        }
        default:
          return '/';
      }
    };
  }

  const options = useMemo(() => {
    const opts: DropdownItemElementType[] = [];

    opts.push({ text: t('groupByMobile'), disabled: true, onClick: () => {} });
    opts.push({
      text: t('groupBy.custom'),
      disabled: groupBy === 'Custom',
      onClick: onOptionClick('Custom'),
    });

    if (!isSectionHidden(NavigationNodeId.KEY_FOCUS_AREAS)) {
      opts.push({
        text:
          getSectionTitle(NavigationNodeId.KEY_FOCUS_AREAS) || t('groupBy.KFA'),
        disabled: groupBy === 'Key Focus Area',
        onClick: onOptionClick('Key Focus Area'),
      });
    }

    opts.push({
      text: t('groupBy.none'),
      disabled: groupBy === 'None',
      onClick: onOptionClick('None'),
    });

    return opts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, navigation, t]);

  const getGroupByI18n = useCallback(() => {
    let groupByI18nKey = t(GROUP_BY_I18N_KEY[groupBy]);
    const KFACustomName = getSectionTitle(NavigationNodeId.KEY_FOCUS_AREAS);
    if (
      groupBy === 'Key Focus Area' &&
      KFACustomName &&
      KFACustomName !== 'Key Focus Area'
    ) {
      groupByI18nKey = KFACustomName;
    }
    return groupByI18nKey;
  }, [getSectionTitle, groupBy, t]);

  return {
    options,
    selectedOption: getGroupByI18n(),
  };
};

export default useGroupDropdown;
